/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import { getCoreRowModel, Row, useReactTable } from "@tanstack/react-table";
import {
    ColumnDef,
    Button,
    DatePicker,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Pagination,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Table,
    Dialog,
    DialogContent,
    Checkbox,
    SearchInput,
    MoreButton,
    Badge,
    AlertDialog,
    AlertDialogContent,
    Card,
} from "@jhool-io/fe-components";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import {
    IBatchRemit,
    RemitSource,
    RemitStatus,
} from "../../types/remits.types";
import {
    cn,
    convertDateFilterStringToDate,
    formatDate,
    getTotalAmountOfTableRowsSelected,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    showMoneyInAppFormat,
    truncateString,
} from "../../../../utils/helpers";
import { useFetchInsuranceProviders } from "../../../../hooks/queries/client";
import { useFetchUserDetails } from "../../../../hooks/queries/user";
import { useFetchBatchRemits } from "../../hooks/remits.queries";
import ListState from "../../../../components/ListState/ListState";
import Navbar from "../../../../components/Navbar/Navbar";
import ManualRemitForm from "../ManualRemitForm/ManualRemitForm";
import useToast from "../../../../hooks/useToast";
import {
    useAddEditBatchRemitAssignee,
    useDeleteManualBatchRemit,
    useUpdateRemitStatus,
} from "../../hooks/remits.mutations";
import { LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";
import {
    getBatchRemitAssigneeToDisplay,
    getRemitClaimsListNavigateUrl,
} from "../../helpers/helpers.remits";
import EditManualRemitForm from "../EditManualRemitForm/EditManualRemitForm";
import EditIcon from "../../../../icons/Edit";
import CheckIcon from "../../../../icons/Check";
import TimesIcon from "../../../../icons/Times";
import Loader from "../../../../shared-ui/Loader/Loader";
import FilterIcon from "../../../../components/Icons/Filter";
import TableFiltersDisplay from "../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import InsuranceProviderFilter from "../../../../shared-ui/InsuranceProviderFilter/InsuranceProviderFilter";

type SubmitTypes = "save" | "save-continue";

type Dialogs = "add_remit" | "delete_remit" | "edit_remit";

export default function RemitsList() {
    const navigate = useNavigate();
    const location = useLocation();

    const [searchParams, setSearchParams] = useSearchParams();
    const [rowSelection, setRowSelection] = React.useState({});
    const [getSelectedRemitsData, setGetSelectedRemitsData] = React.useState<
        Row<IBatchRemit>[]
    >([]);
    const [submitType, setSubmitType] = React.useState<SubmitTypes | null>(
        null
    );
    const [dialogInView, setDialogInView] = React.useState<Dialogs | null>(
        null
    );
    const [selectedManualRemitId, setSelectedManualRemitId] = React.useState<
        string | null
    >(null);

    const [remitToUpdateId, setRemitToUpdateId] = React.useState<
        string | null
    >();

    const [selectedInsurance, setSelectedInsurance] = React.useState<
        string | null
    >();

    const [columnVisibility, setColumnVisibility] = React.useState<
        Record<string, boolean>
    >({});

    const insuranceProviders = useFetchInsuranceProviders();

    const pageFilter = Number(searchParams.get("page") || 1);

    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;
    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");
    const insuranceProviderIdsFilter =
        searchParams.getAll("insurance_providers[]") || [];
    const statusFilter = searchParams.getAll("status[]") || [];
    const hasPaymentsFilter = searchParams.get("has_payments");
    const hasClaimsFilter = searchParams.get("has_claims") || "yes";
    const remitSourceFilter = searchParams.get("source") || "";
    const searchString = searchParams.get("search") || "";
    const dosFromDateFilter = searchParams.get("dos_from_date");
    const dosToDateFilter = searchParams.get("dos_to_date");

    const fromDateFilterToDate = convertDateFilterStringToDate(fromDateFilter);
    const toDateFilterToDate = convertDateFilterStringToDate(toDateFilter);
    const dateOfServiceFromDateFilterToDate =
        convertDateFilterStringToDate(dosFromDateFilter);
    const dateOfServiceToDateFilterToDate =
        convertDateFilterStringToDate(dosToDateFilter);

    const [sortConfig, setSortConfig] = React.useState<{
        key: string;
        direction: string;
    }>({
        key: searchParams.get("sort_attr") || "",
        direction: searchParams.get("sort_order") || "",
    });
    const remitStatusOptions = ["on_hold", "posted", "unposted"];
    const sortableColumns = [
        "remit_received_date",
        "no_of_matched_payments",
        "no_of_unmatched_payments",
        "remit_amount",
        "remit_batch_status",
        "no_of_claims_payments",
        "insurance_provider_name",
        "remit_batch_ref_id",
    ];

    // Column filters
    const remitReceivedDateColFilter = searchParams.get(
        "remit_received_date_filter"
    );
    const remitIdColFilter = searchParams.get("remit_id_filter");
    const refNumberColFilter = searchParams.get("ref_number_filter");
    const insuranceProviderNameColFilter = searchParams.get(
        "insurance_provider_name_filter"
    );
    const remitStatusColFilter = searchParams.get("remit_status_filter");
    const remitSourceColFilter = searchParams.get("remit_source_filter");
    const remitAmountColFilter = searchParams.get("remit_amount_filter");
    const unmatchedAmountColFilter = searchParams.get(
        "unmatched_amount_filter"
    );
    const matchedAmountColFilter = searchParams.get("matched_amount_filter");
    const noOfClaimsPaymentsColFilter = searchParams.get(
        "no_of_claims_payments_filter"
    );
    const noOfUnmatchedPaymentsColFilter = searchParams.get(
        "no_of_unmatched_payments_filter"
    );
    const noOfMatchedPaymentsColFilter = searchParams.get(
        "no_of_matched_payments_filter"
    );

    const handleDefaultHasPaymentsFilter = () => {
        if (hasPaymentsFilter === "yes") {
            return "true";
        }

        if (hasPaymentsFilter === "no") {
            return "false";
        }

        return "";
    };

    const handleDefaultHasClaimsFilter = () => {
        if (hasClaimsFilter === "yes") {
            return "true";
        }

        if (hasClaimsFilter === "no") {
            return "false";
        }

        return "";
    };

    const handleDefaultStatusFilter = () => {
        return statusFilter.length > 0 ? statusFilter : [RemitStatus.UN_POSTED];
    };

    const handleConfigAttribute = () => {
        if (sortableColumns.includes(sortConfig.key)) {
            return sortConfig.key;
        }

        return undefined;
    };

    const { data, error, isLoading, isSuccess } = useFetchBatchRemits({
        batch_remit_status: handleDefaultStatusFilter(),
        insurance_provider_ids: insuranceProviderIdsFilter,
        from_date: fromDateFilter,
        to_date: toDateFilter,
        page: pageFilter,
        limit: limitFilter,
        sort_attribute:
            searchParams.get("sort_attr") || handleConfigAttribute(),
        sort_order: searchParams.get("sort_order") || sortConfig.direction,
        has_payments: handleDefaultHasPaymentsFilter(),
        has_claims: handleDefaultHasClaimsFilter(),
        remit_source: remitSourceFilter,
        dos_from_date: dosFromDateFilter,
        dos_to_date: dosToDateFilter,
        search_string: searchString,
    });

    const remitReceivedDatesInRemit = React.useMemo(() => {
        return Array.from(
            new Set(
                data?.data
                    ?.map((remit) => remit.remit_received_date)
                    .filter(Boolean)
            )
        );
    }, [data?.data]);

    const remitIdsInRemit = React.useMemo(() => {
        return Array.from(
            new Set(
                data?.data
                    ?.map((remit) => remit.remit_batch_ref_id)
                    .filter(Boolean)
            )
        );
    }, [data?.data]);

    const refNumbersInRemit = React.useMemo(() => {
        return Array.from(
            new Set(
                data?.data
                    ?.map((remit) => remit.remit_batch_payment_ref)
                    .filter(Boolean)
            )
        );
    }, [data?.data]);

    const insuranceProvidersInRemit = React.useMemo(() => {
        return (
            data?.data?.reduce(
                (acc: { id: string; name: string }[], remit: IBatchRemit) => {
                    if (
                        !acc.some(
                            (provider) =>
                                provider.id === remit.insurance_provider_id
                        )
                    ) {
                        acc.push({
                            id: remit.insurance_provider_id ?? "",
                            name: remit.insurance_provider_name,
                        });
                    }
                    return acc;
                },
                []
            ) || []
        );
    }, [data?.data]);

    const remitStatusInRemit = React.useMemo(() => {
        return Array.from(
            new Set(
                data?.data
                    ?.map((remit) => remit.remit_batch_status)
                    .filter(Boolean)
            )
        );
    }, [data?.data]);

    const remitSourceInRemit = React.useMemo(() => {
        return Array.from(
            new Set(
                data?.data?.map((remit) => remit.remit_source).filter(Boolean)
            )
        );
    }, [data?.data]);

    const remitAmountInRemit = React.useMemo(() => {
        return Array.from(
            new Set(data?.data?.map((remit) => remit.remit_amount).sort())
        );
    }, [data?.data]);

    const unmatchedAmountInRemit = React.useMemo(() => {
        return Array.from(
            new Set(
                data?.data
                    ?.map((remit) => remit.amount_of_unmatched_payments)
                    .sort()
            )
        );
    }, [data?.data]);

    const matchedAmountInRemit = React.useMemo(() => {
        return Array.from(
            new Set(
                data?.data
                    ?.map((remit) => remit.amount_of_matched_payments)
                    .sort()
            )
        );
    }, [data?.data]);

    const noOfClaimsPaymentsInRemit = React.useMemo(() => {
        return Array.from(
            new Set(data?.data?.map((remit) => remit.no_of_claims).sort())
        );
    }, [data?.data]);

    const noOfUnmatchedPaymentsInRemit = React.useMemo(() => {
        return Array.from(
            new Set(
                data?.data
                    ?.map((remit) => remit.no_of_unmatched_payments)
                    .sort()
            )
        );
    }, [data?.data]);

    const numberOfMatchedPaymentsInRemit = React.useMemo(() => {
        return Array.from(
            new Set(
                data?.data?.map((remit) => remit.no_of_matched_payments).sort()
            )
        );
    }, [data?.data]);

    const filteredInsuranceProviderNames = insuranceProviders?.data?.data
        ?.filter((prov) =>
            insuranceProviderIdsFilter.includes(prov.insurance_provider_id)
        )
        .map((prov) => prov.alias || prov.name);

    const filteredStatusNames = statusFilter.map((s) =>
        makeStringFirstLetterCapital(removeEnumUnderscore(s))
    );

    const appliedFilters = React.useMemo(() => {
        const filters = new Set<{ key: string; values: string[] }>();

        if (remitReceivedDateColFilter) {
            filters.add({
                key: "remit_received_date_filter",
                values: [remitReceivedDateColFilter],
            });
        }

        if (remitIdColFilter) {
            filters.add({
                key: "remit_id_filter",
                values: [remitIdColFilter],
            });
        }

        if (refNumberColFilter) {
            filters.add({
                key: "ref_number_filter",
                values: [refNumberColFilter],
            });
        }

        if (insuranceProviderNameColFilter) {
            filters.add({
                key: "insurance_column_filter",
                values: [insuranceProviderNameColFilter],
            });
        }

        if (remitStatusColFilter) {
            filters.add({
                key: "remit_status_filter",
                values: [remitStatusColFilter],
            });
        }

        if (remitSourceColFilter) {
            filters.add({
                key: "remit_source_filter",
                values: [remitSourceColFilter],
            });
        }

        if (remitAmountColFilter) {
            filters.add({
                key: "remit_amount_filter",
                values: [showMoneyInAppFormat(remitAmountColFilter)],
            });
        }

        if (unmatchedAmountColFilter) {
            filters.add({
                key: "unmatched_amount_filter",
                values: [showMoneyInAppFormat(unmatchedAmountColFilter)],
            });
        }

        if (matchedAmountColFilter) {
            filters.add({
                key: "matched_amount_filter",
                values: [showMoneyInAppFormat(matchedAmountColFilter)],
            });
        }

        if (noOfClaimsPaymentsColFilter) {
            filters.add({
                key: "no_of_claims_payments_filter",
                values: [noOfClaimsPaymentsColFilter],
            });
        }

        if (noOfUnmatchedPaymentsColFilter) {
            filters.add({
                key: "no_of_unmatched_payments_filter",
                values: [noOfUnmatchedPaymentsColFilter],
            });
        }

        if (noOfMatchedPaymentsColFilter) {
            filters.add({
                key: "no_of_matched_payments_filter",
                values: [noOfMatchedPaymentsColFilter],
            });
        }

        if (
            filteredInsuranceProviderNames &&
            filteredInsuranceProviderNames.length > 0
        ) {
            filters.add({
                key: "insurance_providers[]",
                values: filteredInsuranceProviderNames,
            });
        }

        if (filteredStatusNames && filteredStatusNames.length > 0) {
            filters.add({
                key: "status[]",
                values: filteredStatusNames,
            });
        }

        return Array.from(filters);
    }, [
        insuranceProviderNameColFilter,
        noOfClaimsPaymentsColFilter,
        noOfMatchedPaymentsColFilter,
        noOfUnmatchedPaymentsColFilter,
        refNumberColFilter,
        remitAmountColFilter,
        remitIdColFilter,
        remitReceivedDateColFilter,
        remitSourceColFilter,
        remitStatusColFilter,
        unmatchedAmountColFilter,
        matchedAmountColFilter,
        filteredInsuranceProviderNames,
        filteredStatusNames,
    ]);

    const filteredData = React.useMemo(() => {
        if (!data?.data || !isSuccess) return [];

        let filtered = data.data;

        if (remitReceivedDateColFilter) {
            filtered = filtered.filter(
                (remit) =>
                    formatDate(
                        remit.remit_received_date.replace("+00:00", ""),
                        true
                    ) === remitReceivedDateColFilter
            );
        }

        if (remitIdColFilter) {
            filtered = filtered.filter(
                (remit) => remit.remittance_batch_id === remitIdColFilter
            );
        }

        if (refNumberColFilter) {
            filtered = filtered.filter(
                (remit) => remit.remit_batch_payment_ref === refNumberColFilter
            );
        }

        if (insuranceProviderNameColFilter) {
            filtered = filtered.filter(
                (remit) =>
                    remit.insurance_provider_name ===
                    insuranceProviderNameColFilter
            );
        }

        if (remitStatusColFilter) {
            filtered = filtered.filter(
                (remit) => remit.remit_batch_status === remitStatusColFilter
            );
        }

        if (remitSourceColFilter) {
            filtered = filtered.filter(
                (remit) => remit.remit_source === remitSourceColFilter
            );
        }

        if (remitAmountColFilter) {
            filtered = filtered.filter(
                (remit) =>
                    typeof remit.remit_amount === "number" &&
                    String(remit.remit_amount) === remitAmountColFilter
            );
        }

        if (unmatchedAmountColFilter) {
            filtered = filtered.filter(
                (remit) =>
                    typeof remit.amount_of_unmatched_payments === "number" &&
                    String(remit.amount_of_unmatched_payments) ===
                        unmatchedAmountColFilter
            );
        }

        if (matchedAmountColFilter) {
            filtered = filtered.filter(
                (remit) =>
                    typeof remit.amount_of_matched_payments === "number" &&
                    String(remit.amount_of_matched_payments) ===
                        matchedAmountColFilter
            );
        }

        if (noOfClaimsPaymentsColFilter) {
            filtered = filtered.filter(
                (remit) =>
                    typeof remit.no_of_claims === "number" &&
                    String(remit.no_of_claims) === noOfClaimsPaymentsColFilter
            );
        }

        if (noOfUnmatchedPaymentsColFilter) {
            filtered = filtered.filter(
                (remit) =>
                    typeof remit.no_of_unmatched_payments === "number" &&
                    String(remit.no_of_unmatched_payments) ===
                        noOfUnmatchedPaymentsColFilter
            );
        }

        if (noOfMatchedPaymentsColFilter) {
            filtered = filtered.filter(
                (remit) =>
                    typeof remit.no_of_matched_payments === "number" &&
                    String(remit.no_of_matched_payments) ===
                        noOfMatchedPaymentsColFilter
            );
        }

        return filtered;
    }, [
        data?.data,
        insuranceProviderNameColFilter,
        isSuccess,
        noOfClaimsPaymentsColFilter,
        noOfMatchedPaymentsColFilter,
        noOfUnmatchedPaymentsColFilter,
        refNumberColFilter,
        remitAmountColFilter,
        remitIdColFilter,
        remitReceivedDateColFilter,
        remitSourceColFilter,
        remitStatusColFilter,
        unmatchedAmountColFilter,
        matchedAmountColFilter,
    ]);

    const handleColumnFilter = (paramName: string, value?: string | number) => {
        if (
            typeof value === "undefined" ||
            (typeof value === "string" && value === "")
        ) {
            searchParams.delete(paramName);
        } else if (typeof value === "number") {
            searchParams.set(paramName, String(value));
        } else {
            searchParams.set(
                paramName,
                paramName === "remit_received_date_filter"
                    ? formatDate(value.replace("+00:00", ""), true)
                    : value
            );
        }

        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("limit", String(limit));
        setSearchParams(searchParams);
    };

    // To determine if mutation is currently running, returns 0 || 1
    const isMutating = useIsMutating(["update-remit-status"]);
    // To determine if mutation is currently running, returns 0 || 1
    const isAddManualRemitMutating = useIsMutating(["add-manual-remit"]);
    const queryClient = useQueryClient();

    // Toast for success and error states
    const { toast } = useToast();

    const updateRemit = useUpdateRemitStatus();

    const deleteManualRemit = useDeleteManualBatchRemit();

    const loggedInUser = useFetchUserDetails();
    const addEditBatchRemitAssignee = useAddEditBatchRemitAssignee();

    const remitSourceOptions = ["Manual", "Electronic"];

    const showOpenRemitsBtn =
        getSelectedRemitsData.length > 0 &&
        getSelectedRemitsData.every(
            (remit) =>
                remit.original.remit_batch_status !== RemitStatus.UN_POSTED
        );

    const showCloseRemitsBtn =
        getSelectedRemitsData.length > 0 &&
        getSelectedRemitsData.every(
            (remit) => remit.original.remit_batch_status !== RemitStatus.POSTED
        );

    const showOnHoldRemitsBtn =
        getSelectedRemitsData.length > 0 &&
        getSelectedRemitsData.every(
            (remit) => remit.original.remit_batch_status !== RemitStatus.ON_HOLD
        );

    const showDeleteManualRemitsBtn =
        getSelectedRemitsData.length > 0 &&
        getSelectedRemitsData.length ===
            getSelectedRemitsData.filter(
                (remit) => remit.original.remit_source === RemitSource.MANUAL
            ).length;

    const showAssignRemitsBtn =
        getSelectedRemitsData.length > 0 &&
        getSelectedRemitsData.length ===
            getSelectedRemitsData.filter((remit) => {
                const { isAssignedToMe } = getBatchRemitAssigneeToDisplay(
                    remit.original.batch_remit_assignees,
                    loggedInUser?.data?.user_id ?? ""
                );
                return !isAssignedToMe;
            }).length;

    const showUnassignRemitsBtn =
        getSelectedRemitsData.length > 0 &&
        getSelectedRemitsData.length ===
            getSelectedRemitsData.filter((remit) => {
                const { isAssignedToMe } = getBatchRemitAssigneeToDisplay(
                    remit.original.batch_remit_assignees,
                    loggedInUser?.data?.user_id ?? ""
                );
                return isAssignedToMe;
            }).length;

    const selectedRemitsIds = getSelectedRemitsData.map(
        (item) => item.original.remittance_batch_id
    );

    const handleFromDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date");
        else searchParams.set("from_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem("remitslist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleToDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date");
        else searchParams.set("to_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem("remitslist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleInsuranceProviderIdsChange = (
        insuranceProviderIds: string[]
    ) => {
        searchParams.delete("insurance_providers[]");
        insuranceProviderIds.forEach((insuranceProviderId) =>
            searchParams.append("insurance_providers[]", insuranceProviderId)
        );
        searchParams.set("page", "1");
        localStorage.setItem("remitslist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleStatusChange = (value: string) => {
        if (statusFilter.includes(value)) {
            searchParams.delete("status[]", value);
        } else {
            searchParams.append("status[]", value);
        }
        searchParams.set("page", "1");
        localStorage.setItem("remitslist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSourceChange = (value: string) => {
        searchParams.set("source", value);
        searchParams.set("page", "1");
        localStorage.setItem("remitslist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleHasPaymentsFilterChange = (value: string) => {
        if (value === "") searchParams.delete("has_payments");
        else searchParams.set("has_payments", value);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleHasClaimsFilterChange = (value: string) => {
        searchParams.set("has_claims", value);
        searchParams.set("page", "1");
        localStorage.setItem("remitslist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleDosFromDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("dos_from_date");
        else searchParams.set("dos_from_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem("remitslist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleDosToDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("dos_to_date");
        else searchParams.set("dos_to_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem("remitslist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleRowClick = (row: Row<IBatchRemit>) => {
        navigate(
            `${getRemitClaimsListNavigateUrl(row.original.remittance_batch_id)}`
        );
    };

    // onChange handler for search input
    const handleSetSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === "") searchParams.delete("search");
        else searchParams.set("search", e.target.value);
        localStorage.setItem("remitslist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    // onChange handler for page
    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        localStorage.setItem("remitslist_filters", searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleClearFilters = () => {
        localStorage.removeItem("remitslist_filters");
        navigate(location.pathname);
    };

    // Get remits count
    let remitsCount;
    if (error) remitsCount = "";
    else if (isLoading) remitsCount = "";
    else if (data?.data && data?.data.length === 0) remitsCount = 0;
    else if (data?.data && data?.data.length > 0)
        remitsCount = data.total_count;

    const getHasPaymentsLabel = () => {
        if (hasPaymentsFilter === "yes") {
            return "Show remits with payments";
        }
        if (hasPaymentsFilter === "no") {
            return "Show remits without payments";
        }

        return "Show all remits";
    };

    const getHasClaimsLabel = () => {
        if (hasClaimsFilter === "yes") {
            return "Show remits with claims";
        }
        if (hasClaimsFilter === "no") {
            return "Show remits without claims";
        }

        return "Show all remits";
    };

    // function to change remit status
    const handleChangeStatus = (ids: string[], remitStatus: string) => {
        const changeStatusPayload = {
            remittance_batch_ids: ids,
            status: remitStatus,
        };
        updateRemit.mutate(changeStatusPayload, {
            onSuccess: (res) => {
                queryClient.invalidateQueries(["batch-remits"]);
                toast({
                    mode: "success",
                    message:
                        res.message || "Remit status updated successfully.",
                });
                setRowSelection({});
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not update remit status.",
                });
            },
        });
    };

    const handleUpdateBatchRemitAssignee = (
        status: string,
        remittanceBatchIds?: string[]
    ) => {
        const dataToSend = {
            remittance_batch_ids: remittanceBatchIds || selectedRemitsIds,
            status,
        };
        addEditBatchRemitAssignee.mutate(dataToSend, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries(["batch-remits"]);
                toast({
                    mode: "success",
                    message: res.message || "Batch remit assignment updated!",
                });
                setRowSelection({});
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not perform action.",
                });
            },
        });
    };

    const handleChangeInsurance = (remit: IBatchRemit) => {
        const changeInsurancePayload = {
            remittance_batch_ids: [remit.remittance_batch_id],
            insurance_provider_id:
                selectedInsurance || remit.insurance_provider_id,
        };
        updateRemit.mutate(changeInsurancePayload, {
            onSuccess: (res) => {
                queryClient.invalidateQueries(["batch-remits"]);
                toast({
                    mode: "success",
                    message:
                        res.message ||
                        "Insurance provider updated successfully.",
                });
                setRemitToUpdateId(null);
                setSelectedInsurance(null);
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not update insurance provider.",
                });
            },
        });
    };

    const handleDeleteManualRemits = () => {
        const dataToSend = {
            batch_remit_ids: selectedManualRemitId
                ? [selectedManualRemitId]
                : selectedRemitsIds,
        };

        deleteManualRemit.mutate(dataToSend, {
            onSuccess: (res) => {
                queryClient.invalidateQueries(["batch-remits"]);
                toast({
                    mode: "success",
                    message:
                        res.message || "Manual remit(s) deleted successfully.",
                });
                setDialogInView(null);
                if (selectedManualRemitId) {
                    setSelectedManualRemitId(null);
                }
                if (getSelectedRemitsData.length > 0) {
                    setRowSelection({});
                }
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not delete manual remit(s) at this tme",
                });
            },
        });
    };

    const columns: ColumnDef<IBatchRemit>[] = [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    handleChange={table.getToggleAllRowsSelectedHandler()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    label=""
                    name="row"
                    value="row"
                    isChecked={table.getIsAllRowsSelected()}
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    handleChange={row.getToggleSelectedHandler()}
                    indeterminate={row.getIsSomeSelected()}
                    label=""
                    name="row"
                    value="row"
                    isChecked={row.getIsSelected()}
                />
            ),
        },
        {
            accessorKey: "remit_received_date",
            header: "CHECK DATE",
            cell: ({ row }) => (
                <span>
                    {row.original.remit_received_date
                        ? formatDate(
                              row.original.remit_received_date.replace(
                                  "+00:00",
                                  ""
                              ),
                              true
                          )
                        : "--"}
                </span>
            ),
            filterComponent: (
                <Dropdown>
                    <DropdownTrigger asChild>
                        <Button
                            variant="can-hover"
                            size="auto"
                            onClick={(e) => e.stopPropagation()}
                            className="px-1 h-6"
                        >
                            <FilterIcon />
                        </Button>
                    </DropdownTrigger>
                    <DropdownContent width={270}>
                        <DropdownItem
                            onClick={(e) => {
                                e.stopPropagation();
                                handleColumnFilter(
                                    "remit_received_date_filter"
                                );
                            }}
                            isSelected={!remitReceivedDateColFilter}
                            closeMenuOnSelect
                        >
                            All
                        </DropdownItem>
                        {remitReceivedDatesInRemit.map((option) => (
                            <DropdownItem
                                key={option}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleColumnFilter(
                                        "remit_received_date_filter",
                                        option
                                    );
                                }}
                                isSelected={
                                    remitReceivedDateColFilter === option
                                }
                            >
                                {formatDate(option.replace("+00:00", ""), true)}
                            </DropdownItem>
                        ))}
                    </DropdownContent>
                </Dropdown>
            ),
        },
        {
            accessorKey: "remit_batch_ref_id",
            header: "REMIT ID",
            cell: ({ row }) => <span>{row.original.remit_batch_ref_id}</span>,
            filterComponent: (
                <Dropdown>
                    <DropdownTrigger asChild>
                        <Button
                            variant="can-hover"
                            size="auto"
                            onClick={(e) => e.stopPropagation()}
                            className="px-1 h-6"
                        >
                            <FilterIcon />
                        </Button>
                    </DropdownTrigger>
                    <DropdownContent width={270}>
                        <DropdownItem
                            onClick={(e) => {
                                e.stopPropagation();
                                handleColumnFilter("remit_id_filter");
                            }}
                            isSelected={!remitIdColFilter}
                            closeMenuOnSelect
                        >
                            All
                        </DropdownItem>
                        {remitIdsInRemit.map((option) => (
                            <DropdownItem
                                key={option}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleColumnFilter(
                                        "remit_id_filter",
                                        option
                                    );
                                }}
                                isSelected={remitIdColFilter === option}
                            >
                                {option}
                            </DropdownItem>
                        ))}
                    </DropdownContent>
                </Dropdown>
            ),
        },
        {
            accessorKey: "remit_batch_payment_ref",
            header: "Ref Number",
            cell: ({ row }) => (
                <span>{row.original.remit_batch_payment_ref || "--"}</span>
            ),
            filterComponent: (
                <Dropdown>
                    <DropdownTrigger asChild>
                        <Button
                            variant="can-hover"
                            size="auto"
                            onClick={(e) => e.stopPropagation()}
                            className="px-1 h-6"
                        >
                            <FilterIcon />
                        </Button>
                    </DropdownTrigger>
                    <DropdownContent width={270}>
                        <DropdownItem
                            onClick={(e) => {
                                e.stopPropagation();
                                handleColumnFilter("ref_number_filter");
                            }}
                            isSelected={!refNumberColFilter}
                            closeMenuOnSelect
                        >
                            All
                        </DropdownItem>
                        {refNumbersInRemit.map((option) => (
                            <DropdownItem
                                key={option}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleColumnFilter(
                                        "ref_number_filter",
                                        option
                                    );
                                }}
                                isSelected={refNumberColFilter === option}
                            >
                                {option}
                            </DropdownItem>
                        ))}
                    </DropdownContent>
                </Dropdown>
            ),
        },
        {
            accessorKey: "insurance_provider_name",
            header: "INSURANCE",
            cell: ({ row }) =>
                remitToUpdateId === row.original.remittance_batch_id ? (
                    <div className="flex items-center gap-x-2">
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton
                                    className="uppercase"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {(insuranceProviders.isLoading &&
                                        "Loading..") ||
                                        (insuranceProviders.error &&
                                            "Error loading providers") ||
                                        (insuranceProviders?.data?.data &&
                                        selectedInsurance
                                            ? truncateString(
                                                  removeEnumUnderscore(
                                                      insuranceProviders?.data?.data.find(
                                                          (provider) =>
                                                              provider.insurance_provider_id ===
                                                              selectedInsurance
                                                      )?.alias || ""
                                                  )
                                              )
                                            : row.original
                                                  .insurance_provider_name)}
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent align="end" width="auto">
                                {insuranceProviders?.data?.data
                                    .sort((a, b) =>
                                        a.alias.localeCompare(b.alias)
                                    )
                                    .map((provider) => (
                                        <DropdownItem
                                            isSelected={insuranceProviderIdsFilter.includes(
                                                provider.insurance_provider_id
                                            )}
                                            key={provider.insurance_provider_id}
                                            onClick={(e) => {
                                                setSelectedInsurance(
                                                    provider.insurance_provider_id
                                                );
                                                e.stopPropagation();
                                            }}
                                        >
                                            {provider.alias}
                                        </DropdownItem>
                                    ))}
                            </DropdownContent>
                        </Dropdown>

                        <Button
                            size="auto"
                            variant="normal"
                            className="ml-2"
                            onClick={(e) => {
                                handleChangeInsurance(row.original);
                                e.stopPropagation();
                            }}
                            disabled={updateRemit.isLoading}
                        >
                            <CheckIcon className="stroke-textLink" />
                        </Button>

                        <Button
                            size="auto"
                            variant="normal"
                            onClick={(e) => {
                                setRemitToUpdateId(null);
                                setSelectedInsurance(null);
                                e.stopPropagation();
                            }}
                            disabled={updateRemit.isLoading}
                        >
                            <TimesIcon className="stroke-danger" />
                        </Button>
                    </div>
                ) : (
                    <div className="flex items-center gap-x-2">
                        <span className="uppercase">
                            {row.original.insurance_provider_name}
                        </span>
                        <Button
                            size="auto"
                            variant="normal"
                            onClick={(e) => {
                                e.stopPropagation();
                                setRemitToUpdateId(
                                    row.original.remittance_batch_id
                                );
                            }}
                        >
                            <EditIcon />
                        </Button>
                    </div>
                ),
            filterComponent: (
                <Dropdown>
                    <DropdownTrigger asChild>
                        <Button
                            variant="can-hover"
                            size="auto"
                            onClick={(e) => e.stopPropagation()}
                            className="px-1 h-6"
                        >
                            <FilterIcon />
                        </Button>
                    </DropdownTrigger>
                    <DropdownContent width={270}>
                        <>
                            <DropdownItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleColumnFilter(
                                        "insurance_provider_name_filter"
                                    );
                                }}
                                isSelected={!insuranceProviderNameColFilter}
                                closeMenuOnSelect
                            >
                                All
                            </DropdownItem>
                            {insuranceProvidersInRemit &&
                                insuranceProvidersInRemit.map((insurance) => (
                                    <DropdownItem
                                        key={insurance.id}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleColumnFilter(
                                                "insurance_provider_name_filter",
                                                insurance.name
                                            );
                                        }}
                                        isSelected={
                                            insurance.name ===
                                            insuranceProviderNameColFilter
                                        }
                                    >
                                        {insurance.name}
                                    </DropdownItem>
                                ))}
                        </>
                    </DropdownContent>
                </Dropdown>
            ),
        },
        {
            accessorKey: "remit_batch_status",
            header: "STATUS",
            cell: ({ row }) => {
                const status = row.original.remit_batch_status;
                const getBadgeType = () => {
                    if (status === RemitStatus.POSTED) return "success";
                    if (status === RemitStatus.ON_HOLD) return "warning";
                    return "neutral";
                };
                return (
                    <Badge
                        type={getBadgeType()}
                        content={removeEnumUnderscore(status)}
                    />
                );
            },
            filterComponent: (
                <Dropdown>
                    <DropdownTrigger asChild>
                        <Button
                            variant="can-hover"
                            size="auto"
                            onClick={(e) => e.stopPropagation()}
                            className="px-1 h-6"
                        >
                            <FilterIcon />
                        </Button>
                    </DropdownTrigger>
                    <DropdownContent width={270}>
                        <DropdownItem
                            onClick={(e) => {
                                e.stopPropagation();
                                handleColumnFilter("remit_status_filter");
                            }}
                            isSelected={!remitStatusColFilter}
                            closeMenuOnSelect
                        >
                            All
                        </DropdownItem>
                        {remitStatusInRemit.map((option) => (
                            <DropdownItem
                                key={option}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleColumnFilter(
                                        "remit_status_filter",
                                        option
                                    );
                                }}
                                isSelected={remitStatusColFilter === option}
                            >
                                {option}
                            </DropdownItem>
                        ))}
                    </DropdownContent>
                </Dropdown>
            ),
        },
        {
            accessorKey: "batch_remit_assignees",
            header: "ASSIGNED TO",
            cell: ({ row }) => {
                const { usersNameToDisplay } = getBatchRemitAssigneeToDisplay(
                    row.original.batch_remit_assignees,
                    loggedInUser?.data?.user_id ?? ""
                );
                return usersNameToDisplay;
            },
        },
        {
            accessorKey: "remit_source",
            header: "REMIT SOURCE",
            cell: ({ row }) => <span>{row.original.remit_source}</span>,
            filterComponent: (
                <Dropdown>
                    <DropdownTrigger asChild>
                        <Button
                            variant="can-hover"
                            size="auto"
                            onClick={(e) => e.stopPropagation()}
                            className="px-1 h-6"
                        >
                            <FilterIcon />
                        </Button>
                    </DropdownTrigger>
                    <DropdownContent width={270}>
                        <DropdownItem
                            onClick={(e) => {
                                e.stopPropagation();
                                handleColumnFilter("remit_source_filter");
                            }}
                            isSelected={!remitSourceColFilter}
                            closeMenuOnSelect
                        >
                            All
                        </DropdownItem>
                        {remitSourceInRemit.map((option) => (
                            <DropdownItem
                                key={option}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleColumnFilter(
                                        "remit_source_filter",
                                        option
                                    );
                                }}
                                isSelected={remitSourceColFilter === option}
                            >
                                {option}
                            </DropdownItem>
                        ))}
                    </DropdownContent>
                </Dropdown>
            ),
        },
        {
            accessorKey: "remit_amount",
            header: "REMIT AMOUNT",
            cell: ({ row }) => (
                <span>{showMoneyInAppFormat(row.original.remit_amount)}</span>
            ),
            filterComponent: (
                <Dropdown>
                    <DropdownTrigger asChild>
                        <Button
                            variant="can-hover"
                            size="auto"
                            onClick={(e) => e.stopPropagation()}
                            className="px-1 h-6"
                        >
                            <FilterIcon />
                        </Button>
                    </DropdownTrigger>
                    <DropdownContent width={270}>
                        <DropdownItem
                            onClick={(e) => {
                                e.stopPropagation();
                                handleColumnFilter("remit_amount_filter");
                            }}
                            isSelected={!remitAmountColFilter}
                            closeMenuOnSelect
                        >
                            All
                        </DropdownItem>
                        {remitAmountInRemit.map((option) => (
                            <DropdownItem
                                key={option}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleColumnFilter(
                                        "remit_amount_filter",
                                        option
                                    );
                                }}
                                isSelected={
                                    typeof option === "number"
                                        ? remitAmountColFilter ===
                                          String(option)
                                        : false
                                }
                            >
                                {showMoneyInAppFormat(option)}
                            </DropdownItem>
                        ))}
                    </DropdownContent>
                </Dropdown>
            ),
        },
        {
            accessorKey: "amount_of_unmatched_payments",
            header: "UNMATCHED AMOUNT",
            cell: ({ row }) => (
                <span>
                    {showMoneyInAppFormat(
                        row.original.amount_of_unmatched_payments
                    )}
                </span>
            ),
            filterComponent: (
                <Dropdown>
                    <DropdownTrigger asChild>
                        <Button
                            variant="can-hover"
                            size="auto"
                            onClick={(e) => e.stopPropagation()}
                            className="px-1 h-6"
                        >
                            <FilterIcon />
                        </Button>
                    </DropdownTrigger>
                    <DropdownContent width={270}>
                        <DropdownItem
                            onClick={(e) => {
                                e.stopPropagation();
                                handleColumnFilter("unmatched_amount_filter");
                            }}
                            isSelected={!unmatchedAmountColFilter}
                            closeMenuOnSelect
                        >
                            All
                        </DropdownItem>
                        {unmatchedAmountInRemit.map((option) => (
                            <DropdownItem
                                key={option}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleColumnFilter(
                                        "unmatched_amount_filter",
                                        option
                                    );
                                }}
                                isSelected={
                                    typeof option === "number"
                                        ? unmatchedAmountColFilter ===
                                          String(option)
                                        : false
                                }
                            >
                                {showMoneyInAppFormat(option)}
                            </DropdownItem>
                        ))}
                    </DropdownContent>
                </Dropdown>
            ),
        },
        {
            accessorKey: "amount_of_matched_payments",
            header: "MATCHED AMOUNT",
            cell: ({ row }) => (
                <span>
                    {showMoneyInAppFormat(
                        row.original.amount_of_matched_payments
                    )}
                </span>
            ),
            filterComponent: (
                <Dropdown>
                    <DropdownTrigger asChild>
                        <Button
                            variant="can-hover"
                            size="auto"
                            onClick={(e) => e.stopPropagation()}
                            className="px-1 h-6"
                        >
                            <FilterIcon />
                        </Button>
                    </DropdownTrigger>
                    <DropdownContent width={270}>
                        <DropdownItem
                            onClick={(e) => {
                                e.stopPropagation();
                                handleColumnFilter("matched_amount_filter");
                            }}
                            isSelected={!matchedAmountColFilter}
                            closeMenuOnSelect
                        >
                            All
                        </DropdownItem>
                        {matchedAmountInRemit.map((option) => (
                            <DropdownItem
                                key={option}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleColumnFilter(
                                        "matched_amount_filter",
                                        option
                                    );
                                }}
                                isSelected={
                                    typeof option === "number"
                                        ? matchedAmountColFilter ===
                                          String(option)
                                        : false
                                }
                            >
                                {showMoneyInAppFormat(option)}
                            </DropdownItem>
                        ))}
                    </DropdownContent>
                </Dropdown>
            ),
        },
        {
            accessorKey: "no_of_claims",
            header: "NO OF CLAIMS",
            cell: ({ row }) => <span>{row.original.no_of_claims}</span>,
            filterComponent: (
                <Dropdown>
                    <DropdownTrigger asChild>
                        <Button
                            variant="can-hover"
                            size="auto"
                            onClick={(e) => e.stopPropagation()}
                            className="px-1 h-6"
                        >
                            <FilterIcon />
                        </Button>
                    </DropdownTrigger>
                    <DropdownContent width={270}>
                        <DropdownItem
                            onClick={(e) => {
                                e.stopPropagation();
                                handleColumnFilter(
                                    "no_of_claims_payments_filter"
                                );
                            }}
                            isSelected={!noOfClaimsPaymentsColFilter}
                            closeMenuOnSelect
                        >
                            All
                        </DropdownItem>
                        {noOfClaimsPaymentsInRemit.map((option) => (
                            <DropdownItem
                                key={option}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleColumnFilter(
                                        "no_of_claims_payments_filter",
                                        option
                                    );
                                }}
                                isSelected={
                                    typeof option === "number"
                                        ? noOfClaimsPaymentsColFilter ===
                                          String(option)
                                        : false
                                }
                            >
                                {option}
                            </DropdownItem>
                        ))}
                    </DropdownContent>
                </Dropdown>
            ),
        },
        {
            accessorKey: "no_of_unmatched_payments",
            header: "UNMATCHED",
            cell: ({ row }) => (
                <span>{row.original.no_of_unmatched_payments}</span>
            ),
            filterComponent: (
                <Dropdown>
                    <DropdownTrigger asChild>
                        <Button
                            variant="can-hover"
                            size="auto"
                            onClick={(e) => e.stopPropagation()}
                            className="px-1 h-6"
                        >
                            <FilterIcon />
                        </Button>
                    </DropdownTrigger>
                    <DropdownContent width={270}>
                        <DropdownItem
                            onClick={(e) => {
                                e.stopPropagation();
                                handleColumnFilter(
                                    "no_of_unmatched_payments_filter"
                                );
                            }}
                            isSelected={!noOfUnmatchedPaymentsColFilter}
                            closeMenuOnSelect
                        >
                            All
                        </DropdownItem>
                        {noOfUnmatchedPaymentsInRemit.map((option) => (
                            <DropdownItem
                                key={option}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleColumnFilter(
                                        "no_of_unmatched_payments_filter",
                                        option
                                    );
                                }}
                                isSelected={
                                    typeof option === "number"
                                        ? noOfUnmatchedPaymentsColFilter ===
                                          String(option)
                                        : false
                                }
                            >
                                {option}
                            </DropdownItem>
                        ))}
                    </DropdownContent>
                </Dropdown>
            ),
        },
        {
            accessorKey: "no_of_matched_payments",
            header: "MATCHED",
            cell: ({ row }) => (
                <span>{row.original.no_of_matched_payments}</span>
            ),
            filterComponent: (
                <Dropdown>
                    <DropdownTrigger asChild>
                        <Button
                            variant="can-hover"
                            size="auto"
                            onClick={(e) => e.stopPropagation()}
                            className="px-1 h-6"
                        >
                            <FilterIcon />
                        </Button>
                    </DropdownTrigger>
                    <DropdownContent width={270}>
                        <DropdownItem
                            onClick={(e) => {
                                e.stopPropagation();
                                handleColumnFilter(
                                    "no_of_matched_payments_filter"
                                );
                            }}
                            isSelected={!noOfMatchedPaymentsColFilter}
                            closeMenuOnSelect
                        >
                            All
                        </DropdownItem>
                        {numberOfMatchedPaymentsInRemit.map((option) => (
                            <DropdownItem
                                key={option}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleColumnFilter(
                                        "no_of_matched_payments_filter",
                                        option
                                    );
                                }}
                                isSelected={
                                    typeof option === "number"
                                        ? noOfMatchedPaymentsColFilter ===
                                          String(option)
                                        : false
                                }
                            >
                                {option}
                            </DropdownItem>
                        ))}
                    </DropdownContent>
                </Dropdown>
            ),
        },
        {
            accessorKey: "remittance_batch_id",
            header: "",
            cell: ({ row }) => (
                <Dropdown>
                    <DropdownTrigger asChild>
                        <MoreButton />
                    </DropdownTrigger>
                    <DropdownContent width="auto" align="end">
                        {row.original.remit_source === RemitSource.MANUAL && (
                            <DropdownItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDialogInView("edit_remit");
                                    setSelectedManualRemitId(
                                        row.original.remittance_batch_id
                                    );
                                }}
                            >
                                Edit remit
                            </DropdownItem>
                        )}
                        {row.original.remit_source === RemitSource.MANUAL && (
                            <DropdownItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDialogInView("delete_remit");
                                    setSelectedManualRemitId(
                                        row.original.remittance_batch_id
                                    );
                                }}
                            >
                                Delete remit
                            </DropdownItem>
                        )}
                        {row.original.remit_batch_status !==
                            RemitStatus.POSTED && (
                            <DropdownItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleChangeStatus(
                                        [row.original.remittance_batch_id],
                                        RemitStatus.POSTED
                                    );
                                }}
                                disabled={isMutating > 0}
                            >
                                Mark as Posted
                            </DropdownItem>
                        )}
                        {row.original.remit_batch_status !==
                            RemitStatus.UN_POSTED && (
                            <DropdownItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleChangeStatus(
                                        [row.original.remittance_batch_id],
                                        RemitStatus.UN_POSTED
                                    );
                                }}
                                disabled={isMutating > 0}
                            >
                                Mark as Unposted
                            </DropdownItem>
                        )}
                        {row.original.remit_batch_status !==
                            RemitStatus.ON_HOLD && (
                            <DropdownItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleChangeStatus(
                                        [row.original.remittance_batch_id],
                                        RemitStatus.ON_HOLD
                                    );
                                }}
                                disabled={isMutating > 0}
                            >
                                Mark as On Hold
                            </DropdownItem>
                        )}
                        <DropdownItem
                            onClick={(e) => {
                                e.stopPropagation();
                                const { isAssignedToMe } =
                                    getBatchRemitAssigneeToDisplay(
                                        row.original.batch_remit_assignees,
                                        loggedInUser?.data?.user_id ?? ""
                                    );
                                handleUpdateBatchRemitAssignee(
                                    isAssignedToMe ? "unlocked" : "in_progress",
                                    [row.original.remittance_batch_id]
                                );
                            }}
                            disabled={addEditBatchRemitAssignee.isLoading}
                            className="normal-case"
                        >
                            {getBatchRemitAssigneeToDisplay(
                                row.original.batch_remit_assignees,
                                loggedInUser?.data?.user_id ?? ""
                            ).isAssignedToMe
                                ? "Unassign"
                                : "Assign to me"}
                        </DropdownItem>
                    </DropdownContent>
                </Dropdown>
            ),
        },
    ];

    React.useEffect(() => {
        searchParams.set("sort_attr", sortConfig.key);
        searchParams.set("sort_order", sortConfig.direction);
        localStorage.setItem("remitslist_filters", searchParams.toString());
        setSearchParams(searchParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortConfig]);

    const table = useReactTable({
        columns,
        data: data?.data || [],
        state: {
            columnVisibility,
        },
        getCoreRowModel: getCoreRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
    });

    return (
        <>
            <AlertDialog open={dialogInView === "delete_remit"}>
                <AlertDialogContent
                    title="Delete manual remit?"
                    description=" This action will delete all individual remits and payments attached to this remit"
                    onCancelClick={() => {
                        setDialogInView(null);
                        setSelectedManualRemitId(null);
                    }}
                    isDeleting
                    saveText="Delete manual remit"
                    onSaveClick={handleDeleteManualRemits}
                    isSaveBtnDisabled={deleteManualRemit.isLoading}
                    isCancelBtnDisabled={deleteManualRemit.isLoading}
                />
            </AlertDialog>

            <Dialog open={dialogInView === "add_remit"}>
                <DialogContent
                    variant="center"
                    title="Add Remit"
                    cancelBtnFormValue="add-remit-form"
                    canCancelButtonSubmit
                    submitBtnFormValue="add-remit-form"
                    handleCloseDialog={() => setDialogInView(null)}
                    cancelText="Save progress"
                    saveText="Continue"
                    onSaveClick={() => {
                        setSubmitType("save-continue");
                    }}
                    isCancelBtnDisabled={isAddManualRemitMutating > 0}
                    isSubmitBtnDisabled={isAddManualRemitMutating > 0}
                    onCancelClick={() => setSubmitType("save")}
                >
                    <ManualRemitForm
                        submitType={submitType}
                        onFormSubmit={() => setDialogInView(null)}
                    />
                </DialogContent>
            </Dialog>

            <Dialog
                open={
                    dialogInView === "edit_remit" &&
                    Boolean(selectedManualRemitId)
                }
            >
                <DialogContent
                    variant="center"
                    handleCloseDialog={() => setDialogInView(null)}
                    submitBtnFormValue="edit-remit-form"
                    saveText="Save changes"
                    title="Edit Manual Remit"
                >
                    <EditManualRemitForm
                        onFormSubmit={() => setDialogInView(null)}
                        remitId={selectedManualRemitId || ""}
                    />
                </DialogContent>
            </Dialog>
            <Navbar title={`Remits List (${remitsCount})`} />
            <div className="page">
                <Card
                    classNames={cn(
                        "flex flex-col gap-[--spacing-items] mb-[--spacing-header]"
                    )}
                >
                    <SearchInput
                        containerClass="w-[400px]"
                        placeholder="Search by client, remit id and ref number"
                        onChange={handleSetSearchString}
                        defaultValue={searchString}
                    />
                    <div className="flex items-center gap-[--spacing-items] flex-wrap justify-between">
                        <div className="flex gap-[--spacing-items] flex-wrap">
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>Status</FilterButton>
                                </DropdownTrigger>
                                <DropdownContent align="end" width="auto">
                                    {remitStatusOptions.map((status) => (
                                        <DropdownItem
                                            isMulti
                                            itemValue={status}
                                            values={statusFilter}
                                            key={status}
                                            onClick={() =>
                                                handleStatusChange(status)
                                            }
                                        >
                                            {makeStringFirstLetterCapital(
                                                removeEnumUnderscore(status)
                                            )}
                                        </DropdownItem>
                                    ))}
                                </DropdownContent>
                            </Dropdown>

                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>
                                        {remitSourceFilter
                                            ? `${truncateString(
                                                  removeEnumUnderscore(
                                                      remitSourceFilter
                                                  )
                                              )} remits`
                                            : "Source"}
                                    </FilterButton>
                                </DropdownTrigger>

                                <DropdownContent align="end" width="auto">
                                    <DropdownItem
                                        isSelected={!remitSourceFilter}
                                        onClick={() => handleSourceChange("")}
                                    >
                                        Show all remits
                                    </DropdownItem>

                                    {remitSourceOptions.map((source) => (
                                        <DropdownItem
                                            isSelected={
                                                remitSourceFilter === source
                                            }
                                            key={source}
                                            onClick={() =>
                                                handleSourceChange(source)
                                            }
                                        >
                                            {`Show ${makeStringFirstLetterCapital(
                                                source
                                            )} remits`}
                                        </DropdownItem>
                                    ))}
                                </DropdownContent>
                            </Dropdown>

                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>
                                        {getHasPaymentsLabel()}
                                    </FilterButton>
                                </DropdownTrigger>
                                <DropdownContent align="end" width="auto">
                                    <DropdownItem
                                        className={cn(
                                            "flex gap-x-8 items-center text-xs font-medium",
                                            {
                                                "text-primary":
                                                    !hasPaymentsFilter,
                                            }
                                        )}
                                        isSelected={!hasPaymentsFilter}
                                        onClick={() =>
                                            handleHasPaymentsFilterChange("")
                                        }
                                    >
                                        Show all remits
                                    </DropdownItem>
                                    <DropdownItem
                                        isSelected={hasPaymentsFilter === "yes"}
                                        onClick={() =>
                                            handleHasPaymentsFilterChange("yes")
                                        }
                                    >
                                        Show remits with payments
                                    </DropdownItem>
                                    <DropdownItem
                                        isSelected={hasPaymentsFilter === "no"}
                                        onClick={() =>
                                            handleHasPaymentsFilterChange("no")
                                        }
                                    >
                                        Show remits without payments
                                    </DropdownItem>
                                </DropdownContent>
                            </Dropdown>

                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>
                                        {getHasClaimsLabel()}
                                    </FilterButton>
                                </DropdownTrigger>
                                <DropdownContent align="end" width="auto">
                                    <DropdownItem
                                        isSelected={hasClaimsFilter === "all"}
                                        onClick={() =>
                                            handleHasClaimsFilterChange("all")
                                        }
                                    >
                                        Show all remits
                                    </DropdownItem>
                                    <DropdownItem
                                        isSelected={hasClaimsFilter === "yes"}
                                        onClick={() =>
                                            handleHasClaimsFilterChange("yes")
                                        }
                                    >
                                        Show remits with claims
                                    </DropdownItem>
                                    <DropdownItem
                                        isSelected={hasClaimsFilter === "no"}
                                        onClick={() =>
                                            handleHasClaimsFilterChange("no")
                                        }
                                    >
                                        Show remits without claims
                                    </DropdownItem>
                                </DropdownContent>
                            </Dropdown>

                            <InsuranceProviderFilter
                                values={insuranceProviderIdsFilter}
                                onChange={handleInsuranceProviderIdsChange}
                                showSelfPay={false}
                            />

                            <Popover>
                                <PopoverTrigger asChild>
                                    <FilterButton>Check Date</FilterButton>
                                </PopoverTrigger>
                                <PopoverContent align="end">
                                    <div className="mb-1">
                                        <DatePicker
                                            label="From"
                                            placeholderText="MM/DD/YYYY"
                                            selected={fromDateFilterToDate}
                                            onChange={
                                                handleFromDateFilterChange
                                            }
                                            maxDate={
                                                toDateFilterToDate ||
                                                new Date(Date.now())
                                            }
                                            isClearable
                                        />
                                    </div>

                                    <DatePicker
                                        label="To"
                                        placeholderText="MM/DD/YYYY"
                                        selected={toDateFilterToDate}
                                        onChange={handleToDateFilterChange}
                                        minDate={fromDateFilterToDate}
                                        isClearable
                                    />
                                </PopoverContent>
                            </Popover>

                            <Popover>
                                <PopoverTrigger asChild>
                                    <FilterButton>Date of service</FilterButton>
                                </PopoverTrigger>
                                <PopoverContent align="end">
                                    <div>
                                        <DatePicker
                                            label="From"
                                            placeholderText="MM/DD/YYYY"
                                            selected={
                                                dateOfServiceFromDateFilterToDate
                                            }
                                            onChange={
                                                handleDosFromDateFilterChange
                                            }
                                            maxDate={
                                                dateOfServiceToDateFilterToDate ||
                                                new Date(Date.now())
                                            }
                                            isClearable
                                        />
                                    </div>

                                    <DatePicker
                                        label="To"
                                        placeholderText="MM/DD/YYYY"
                                        selected={
                                            dateOfServiceToDateFilterToDate
                                        }
                                        onChange={handleDosToDateFilterChange}
                                        minDate={
                                            dateOfServiceFromDateFilterToDate
                                        }
                                        isClearable
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>

                        <div>
                            <Button
                                onClick={() => setDialogInView("add_remit")}
                                className="w-full"
                            >
                                Add remit
                            </Button>
                        </div>
                    </div>
                    <TableFiltersDisplay
                        appliedFilters={appliedFilters}
                        wrapperClass="mb-[--spacing-items]"
                    />
                </Card>

                {error && error?.response?.status !== 404 && (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg={
                            error.response?.data.message ||
                            "Cannot display remits at this time please try again later"
                        }
                    />
                )}
                {isLoading && <Loader />}

                {data && isSuccess && (
                    <>
                        {filteredData.length === 0 && (
                            <ListState
                                stateHelperText="Remits will appear here once available "
                                emptyMessage="No remits yet"
                                isError={false}
                            />
                        )}

                        {filteredData.length > 0 && (
                            <>
                                {(showCloseRemitsBtn ||
                                    showOpenRemitsBtn ||
                                    showOnHoldRemitsBtn ||
                                    showDeleteManualRemitsBtn ||
                                    showAssignRemitsBtn ||
                                    showUnassignRemitsBtn) &&
                                    getTotalAmountOfTableRowsSelected(
                                        rowSelection
                                    ).amount > 0 && (
                                        <div className="flex items-center mb-[--spacing-items] gap-[--spacing-items] flex-wrap">
                                            <div className="font-medium text-base shrink-0">
                                                {
                                                    getTotalAmountOfTableRowsSelected(
                                                        rowSelection
                                                    ).amountString
                                                }
                                                :
                                            </div>
                                            {showOpenRemitsBtn && (
                                                <Button
                                                    variant="secondary"
                                                    onClick={() =>
                                                        handleChangeStatus(
                                                            selectedRemitsIds,
                                                            RemitStatus.UN_POSTED
                                                        )
                                                    }
                                                >
                                                    Mark as Unposted
                                                </Button>
                                            )}
                                            {showCloseRemitsBtn && (
                                                <Button
                                                    variant="secondary"
                                                    onClick={() =>
                                                        handleChangeStatus(
                                                            selectedRemitsIds,
                                                            RemitStatus.POSTED
                                                        )
                                                    }
                                                >
                                                    Mark as Posted
                                                </Button>
                                            )}
                                            {showAssignRemitsBtn && (
                                                <Button
                                                    variant="secondary"
                                                    onClick={() =>
                                                        handleUpdateBatchRemitAssignee(
                                                            "in_progress"
                                                        )
                                                    }
                                                    disabled={
                                                        addEditBatchRemitAssignee.isLoading
                                                    }
                                                >
                                                    Assign remits to me
                                                </Button>
                                            )}
                                            {showUnassignRemitsBtn && (
                                                <Button
                                                    variant="secondary"
                                                    onClick={() =>
                                                        handleUpdateBatchRemitAssignee(
                                                            "unlocked"
                                                        )
                                                    }
                                                    disabled={
                                                        addEditBatchRemitAssignee.isLoading
                                                    }
                                                >
                                                    Unassign remits
                                                </Button>
                                            )}
                                            {showOnHoldRemitsBtn && (
                                                <Button
                                                    variant="secondary"
                                                    onClick={() =>
                                                        handleChangeStatus(
                                                            selectedRemitsIds,
                                                            RemitStatus.ON_HOLD
                                                        )
                                                    }
                                                >
                                                    Mark as On Hold
                                                </Button>
                                            )}
                                            {showDeleteManualRemitsBtn && (
                                                <Button
                                                    onClick={() =>
                                                        setDialogInView(
                                                            "delete_remit"
                                                        )
                                                    }
                                                    className="bg-danger hover:bg-danger"
                                                >
                                                    Delete remits
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                <Table
                                    columns={columns}
                                    data={filteredData}
                                    handleRowClick={handleRowClick}
                                    setRowSelection={setRowSelection}
                                    rowSelection={rowSelection}
                                    setGetSelectedData={
                                        setGetSelectedRemitsData
                                    }
                                    hasPagination={data.total_count > 100}
                                    isHeaderFixed
                                    sortableColumns={sortableColumns}
                                    sortConfig={sortConfig}
                                    setSortConfig={setSortConfig}
                                    showSizeFilter
                                    isColumnVisible={columnVisibility}
                                    showColumnVisibilityFilter
                                    tableHeader={
                                        searchParams.size > 0 ? (
                                            <div>
                                                <Button
                                                    size="auto"
                                                    variant="normal"
                                                    onClick={handleClearFilters}
                                                    iconLeft={<TimesIcon />}
                                                >
                                                    Clear filters
                                                </Button>
                                            </div>
                                        ) : null
                                    }
                                    columnVisibilityDropdownItems={
                                        <>
                                            {table
                                                .getAllLeafColumns()
                                                .filter(
                                                    (col) => col.id !== "select"
                                                )
                                                .map((column) => {
                                                    return (
                                                        <DropdownItem
                                                            key={column.id}
                                                            onClick={() =>
                                                                column.toggleVisibility()
                                                            }
                                                            className="*:!uppercase"
                                                            isMulti
                                                            itemValue={
                                                                column.id
                                                            }
                                                            values={table
                                                                .getVisibleLeafColumns()
                                                                .map(
                                                                    (col) =>
                                                                        col.id
                                                                )}
                                                        >
                                                            {column?.columnDef
                                                                ?.header
                                                                ? `${
                                                                      column?.parent?.id?.trim()
                                                                          ? `${column.parent.id} `
                                                                          : ""
                                                                  }${
                                                                      column
                                                                          .columnDef
                                                                          .header
                                                                  }`
                                                                : column.id?.replaceAll(
                                                                      "_",
                                                                      " "
                                                                  )}
                                                        </DropdownItem>
                                                    );
                                                })}
                                        </>
                                    }
                                    pagination={
                                        <Pagination
                                            totalCount={data.total_count}
                                            totalPages={data.total_pages}
                                            currentPage={pageFilter}
                                            onPageChange={handlePageChange}
                                            limit={limitFilter}
                                            onLimitChange={handleLimitChange}
                                        />
                                    }
                                />
                            </>
                        )}
                    </>
                )}
            </div>
        </>
    );
}
